import { db } from '../firebase';
import { collection, addDoc, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';

export const ShareService = {
  // Current WhatsApp sharing functionality
  shareViaWhatsApp: (event, formatDateTime, displayDate) => {
    let displayDateStr = "";
    if (displayDate) {
      displayDateStr = displayDate.toLocaleDateString(undefined, { 
        month: 'long', 
        day: 'numeric'
      });
    }

    let eventDetails = `Event: ${event.eventName}\n`;
    
    if (displayDateStr) {
      eventDetails += `Date: ${displayDateStr}\n`;
    }
    eventDetails += `Time: ${formatDateTime(event)}\n`;

    if (event.location) {
      eventDetails += `Location: ${event.location}\n`;
    }

    if (event.participant) {
      eventDetails += `With: ${event.participant}\n`;
    } else if (event.participants && Array.isArray(event.participants) && event.participants.length > 0) {
      eventDetails += `With: ${event.participants.join(', ')}\n`;
    }

    if (event.description) {
      eventDetails += `\n${event.description}`;
    }

    if (displayDate) {
      const year = displayDate.getFullYear();
      const month = String(displayDate.getMonth() + 1).padStart(2, '0');
      const day = String(displayDate.getDate()).padStart(2, '0');
      const dateParam = `${year}-${month}-${day}`;
      
      eventDetails += `\n\nView in calendar: https://homi-5e2ac.web.app/calendar?date=${dateParam}`;
    } else {
      eventDetails += `\n\nView in calendar: https://homi-5e2ac.web.app/calendar`;
    }

    window.open(`https://wa.me/?text=${encodeURIComponent(eventDetails)}`, '_blank');
  },

  // WhatsApp sharing with signup prompt
  shareViaWhatsAppWithSignup: (event, formatDateTime, displayDate) => {
    let eventDetails = `Join Homi to manage your events easily!\nSign up at: https://homi-5e2ac.web.app/signup`;

    window.open(`https://wa.me/?text=${encodeURIComponent(eventDetails)}`, '_blank');
  },

  // Share with another app user
  shareWithUser: async (event, recipientEmail, senderUserId) => {
    try {
      // Generate a unique shared ID
      const sharedId = `shared_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(recipientEmail)) {
        throw new Error('Please enter a valid email address.');
      }
      
      // Check if recipient user exists in the users collection
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', recipientEmail.toLowerCase())
      );
      
      const userSnapshot = await getDocs(usersQuery);
      
      if (userSnapshot.empty) {
        throw new Error('User not found with this email address. Please make sure they have signed up for Homi.');
      }
      
      const recipientUser = userSnapshot.docs[0];
      const recipientUserId = recipientUser.data().uid;
      
      // Create the shared event object with sharedId
      const sharedEvent = {
        ...event,
        sharedId: sharedId,
        sharedBy: senderUserId,
        sharedWith: recipientUserId,
        sharedWithEmail: recipientEmail.toLowerCase(),
        sharedAt: new Date().toISOString(),
        status: 'pending', // pending, accepted, declined
        originalEventId: event.id,
        userId: null, // Will be set when user accepts the share
        isSharedEvent: true,
        source: 'shared',
        createdAt: new Date().toISOString()
      };
      
      // Remove the original ID so Firestore generates a new one
      delete sharedEvent.id;
      
      // Add the shared event to the global pendingEvents collection
      const docRef = await addDoc(
        collection(db, 'pendingEvents'),
        sharedEvent
      );
      
      // Optional: Update the original event to track that it's been shared
      // (only if the event exists in the sender's accepted events)
      try {
        const senderEventsQuery = query(
          collection(db, 'acceptedEvents'),
          where('userId', '==', senderUserId)
        );
        
        const senderEventSnapshot = await getDocs(senderEventsQuery);
        
        // Find the matching event by properties since IDs might be different
        const matchingEvent = senderEventSnapshot.docs.find(doc => {
          const data = doc.data();
          return data.eventName === event.eventName &&
                 data.day === event.day &&
                 data.month === event.month &&
                 data.year === event.year;
        });
        
        if (matchingEvent) {
          await updateDoc(matchingEvent.ref, {
            sharedWith: recipientEmail,
            sharedWithUserId: recipientUserId,
            sharedAt: new Date().toISOString(),
            sharedId: sharedId
          });
        }
      } catch (updateError) {
        console.warn('Could not update original event with sharing info:', updateError);
        // Non-critical error, don't fail the sharing operation
      }
      
      return {
        success: true,
        sharedId: sharedId,
        message: `Event shared successfully with ${recipientEmail}!`
      };
      
    } catch (error) {
      console.error('Error sharing event:', error);
      return {
        success: false,
        error: error.message || 'Failed to share event'
      };
    }
  },

  // Accept a shared event
  acceptSharedEvent: async (sharedEventId, userId) => {
    try {
      // Move event from pendingEvents to events collection
      // This would be implemented when the accept functionality is needed
      return { success: true };
    } catch (error) {
      console.error('Error accepting shared event:', error);
      return { success: false, error: error.message };
    }
  },

  // Decline a shared event
  declineSharedEvent: async (sharedEventId, userId) => {
    try {
      // Remove event from pendingEvents collection
      // This would be implemented when the decline functionality is needed
      return { success: true };
    } catch (error) {
      console.error('Error declining shared event:', error);
      return { success: false, error: error.message };
    }
  },

  // Share event with multiple users
  shareEventWithUsers: async (eventData, selectedUsers) => {
    try {
      const sharePromises = [];
      
      for (const user of selectedUsers) {
        const sharedEvent = {
          ...eventData,
          sharedId: `shared_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          sharedWith: user.uid,
          sharedWithEmail: user.email,
          sharedAt: new Date().toISOString(),
          status: 'pending',
          userId: user.uid,
          isSharedEvent: true,
          source: 'shared',
          createdAt: new Date().toISOString()
        };
        
        // Remove any existing ID
        delete sharedEvent.id;
        
        sharePromises.push(
          addDoc(collection(db, 'pendingEvents'), sharedEvent)
        );
      }
      
      await Promise.all(sharePromises);
      
      return {
        success: true,
        message: `Event shared with ${selectedUsers.length} user(s)`
      };
    } catch (error) {
      console.error('Error sharing event with users:', error);
      throw error;
    }
  }
};